import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Paintbrush, Fingerprint, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from './LanguageToggle';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when location changes (page navigation)
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const navLinks = [
    { path: '/', label: t('header.home') },
    { path: '/tattoo-design', label: t('header.tattooDesign') },
    { path: '/nail-design', label: t('header.nailDesign') },
    { path: '/gallery', label: t('header.gallery') },
    { path: '/about', label: t('header.about') },
  ];

  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled || isMenuOpen ? 'bg-gray-900/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-1 z-20">
          <div className="flex items-center">
            <Paintbrush className="h-6 w-6 md:h-8 md:w-8 text-purple-500" />
            <Fingerprint className="h-6 w-6 md:h-8 md:w-8 text-pink-500 -ml-1" />
          </div>
          <span className="text-lg md:text-2xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
            TattooNailGenius
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-6">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`transition-colors hover:text-pink-500 ${
                location.pathname === link.path ? 'text-pink-500' : 'text-gray-100'
              }`}
            >
              {link.label}
            </Link>
          ))}
          
          <div className="flex items-center">
            <LanguageToggle />
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-2 z-20">
          <LanguageToggle />
          
          <button 
            className="bg-gray-800 text-gray-100 hover:text-pink-500 p-1.5 rounded-md focus:outline-none"
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu - improved visibility and transition */}
      <div 
        className={`md:hidden fixed inset-0 bg-gray-900/95 backdrop-blur-md z-10 transition-all duration-300 ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="container mx-auto px-4 pt-24 pb-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`block py-4 text-lg font-medium hover:text-pink-500 ${
                location.pathname === link.path ? 'text-pink-500' : 'text-gray-100'
              }`}
              onClick={closeMenu}
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
};

export default Header;